<template>
  <div class="project-container">
    <header class="header">
      <div class="flex-container">
        <div class="image-container">
          <img :src="require('@/assets/image/referendum-logo.png')" alt="Logo Referendum" class="header-image"
               loading="lazy"/>
        </div>
        <div class="text-container">
          <h1>{{ $t('referendum.title') }}</h1>
          <p class="subtitle">{{ $t('referendum.subtitle') }}</p>
        </div>
      </div>
    </header>

    <section class="about-project">
      <h2>{{ $t('referendum.aboutTitle') }}</h2>
      <div class="content-grid">
        <div class="text-block">
          <p v-html="$t('referendum.about1')"></p>
          <p v-html="$t('referendum.about2')"></p>
          <p>{{ $t('referendum.about3') }}</p>
        </div>
        <div class="highlight-box">
          <div class="stat-item">
            <span class="mdi mdi-shield-lock-outline stat-icon"></span>
            <span class="stat-text">ElGamal Encryption</span>
          </div>
          <div class="stat-item">
            <span class="mdi mdi-lan-connect stat-icon"></span>
            <span class="stat-text">TLS Secured Sockets</span>
          </div>
          <div class="stat-item">
            <span class="mdi mdi-file-tree-outline stat-icon"></span>
            <span class="stat-text">Modular Gradle Monolith</span>
          </div>
        </div>
      </div>
    </section>

    <section class="architecture-details">
      <h2>{{ $t('referendum.techTitle') }}</h2>
      <div class="architecture-visual">
        <div class="visual-header">
          <span class="visual-badge">System Workflow</span>
          <h3>{{ $t('referendum.techWorkflow') }}</h3>
        </div>
        <div class="mermaid-container" id="voting-diagram">
          <!-- Flow of voting process -->
        </div>
        <p class="visual-caption">{{ $t('referendum.techCaption') }}</p>
      </div>

      <div class="architecture-visual docker-visual">
        <div class="visual-header">
          <span class="visual-badge">Deployment Blueprint</span>
          <h3>{{ $t('referendum.dockerTitle') }}</h3>
        </div>
        <div class="mermaid-container" id="docker-diagram">
          <!-- Docker compose architecture -->
        </div>
        <p class="visual-caption">{{ $t('referendum.dockerCaption') }}</p>
      </div>

      <div class="tech-grid">
        <div class="tech-card">
          <span class="mdi mdi-swap-horizontal tech-icon"></span>
          <div class="tech-text">
            <h4>Sockets & Sprints</h4>
            <ul>
              <li v-html="$t('referendum.tech1')"></li>
              <li v-html="$t('referendum.tech4')"></li>
            </ul>
          </div>
        </div>
        <div class="tech-card">
          <span class="mdi mdi-security tech-icon"></span>
          <div class="tech-text">
            <h4>Sécurité Avancée</h4>
            <ul>
              <li v-html="$t('referendum.tech2')"></li>
              <li v-html="$t('referendum.tech3')"></li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="code-peek">
      <h2>{{ $t('referendum.codeTitle') }}</h2>
      <p class="code-intro">{{ $t('referendum.codeCaption') }}</p>
      
      <div class="code-container">
        <div class="code-header">
          <div class="dots">
            <span class="dot red"></span>
            <span class="dot yellow"></span>
            <span class="dot green"></span>
          </div>
          <span class="file-path">{{ $t('referendum.codeFile1') }}</span>
        </div>
        <div class="code-content">
          <pre><code><span class="k">public class</span> <span class="c">DecryptCryptoServiceImpl</span> <span class="k">implements</span> <span class="c">DecryptCryptoService</span> {
    <span class="k">@Override</span>
    <span class="k">public</span> <span class="c">ResultatDecryptModel</span> <span class="f">decryt</span>(<span class="c">ChiffrerDecryptModel</span> chiffrer, <span class="c">List</span>&lt;<span class="c">BigInteger</span>&gt; prepares, <span class="c">BigInteger</span> p, <span class="c">BigInteger</span> g) {
        <span class="c">BigInteger</span> prepare = <span class="c">BigInteger</span>.<span class="c">ONE</span>;
        <span class="k">for</span> (<span class="c">BigInteger</span> i : prepares) {
            prepare = prepare.<span class="f">multiply</span>(i);
        }

        <span class="c">BigInteger</span> gPowSommeVi = chiffrer.<span class="f">getV</span>().<span class="f">multiply</span>(prepare.<span class="f">modInverse</span>(p)).<span class="f">mod</span>(p);
        <span class="c">BigInteger</span> MTest;
        <span class="c">BigInteger</span> B = <span class="c">BigInteger</span>.<span class="c">TWO</span>.<span class="f">pow</span>(chiffrer.<span class="f">getNbVotes</span>()).<span class="f">subtract</span>(<span class="c">BigInteger</span>.<span class="c">ONE</span>);
        <span class="k">for</span> (<span class="c">BigInteger</span> i = <span class="c">BigInteger</span>.<span class="c">ZERO</span>; i.<span class="f">compareTo</span>(B) &lt;= <span class="s">0</span>; i = i.<span class="f">add</span>(<span class="c">BigInteger</span>.<span class="c">ONE</span>)) {
            MTest = g.<span class="f">modPow</span>(i, p);
            <span class="k">if</span> (MTest.<span class="f">equals</span>(gPowSommeVi)) {
                <span class="k">return new</span> <span class="c">ResultatDecryptModel</span>(i.<span class="f">intValue</span>(), chiffrer.<span class="f">getNbVotes</span>() - i.<span class="f">intValue</span>());
            }
        }
        <span class="k">throw new</span> <span class="c">RuntimeException</span>(<span class="s">"No solution found"</span>);
    }
}</code></pre>
        </div>
      </div>

      <div class="code-container second-code">
        <div class="code-header">
          <div class="dots">
            <span class="dot red"></span>
            <span class="dot yellow"></span>
            <span class="dot green"></span>
          </div>
          <span class="file-path">{{ $t('referendum.codeFile2') }}</span>
        </div>
        <div class="code-content">
          <pre><code><span class="k">@Override</span>
<span class="k">public void</span> <span class="f">aNewDecrypt</span>(<span class="c">UUID</span> referendumId, <span class="c">UUID</span> questionId, <span class="c">PrepareDto</span> prepare, <span class="c">UUID</span> userId) {
    <span class="c">LocalDateTime</span> now = <span class="c">LocalDateTime</span>.<span class="f">now</span>(<span class="c">ZoneOffset</span>.<span class="f">ofHours</span>(<span class="s">2</span>));
    <span class="c">ReferendumModel</span> referendumModel = referendumService.<span class="f">findReferendumByIdStarted</span>(referendumId);
    
    <span class="k">if</span> (referendumModel.<span class="f">getEndDate</span>().<span class="f">isAfter</span>(now)) {
        <span class="k">throw new</span> <span class="c">IllegalArgumentException</span>(<span class="s">"Le referendum n'est pas terminé"</span>);
    }
    
    <span class="t">// ... Validation et stockage de la part de déchiffrement</span>
    decryptRepository.<span class="f">insertPrepare</span>(chiffrerId, pkId, <span class="k">new</span> <span class="c">BigInteger</span>(prepare.<span class="f">getPrepare</span>()));
    
    <span class="t">// Si tous les scrutateurs ont envoyé leur part</span>
    <span class="k">if</span> (decryptRepository.<span class="f">nbPrepared</span>(chiffrerId) == referendumModel.<span class="f">getPk_ids</span>().<span class="f">size</span>()) {
        <span class="c">ResultatDecryptModel</span> result = decryptCryptoService.<span class="f">decryt</span>(chiffrerDecryptModel, prepares, p, g);
        resultatService.<span class="f">insertResultat</span>(questionId, result);
    }
}</code></pre>
        </div>
      </div>
    </section>

    <section class="interfaces">
      <h2>Interfaces & Expérience</h2>
      <div class="interface-grid">
        <div class="interface-block">
          <h3>{{ $t('referendum.adminTitle') }}</h3>
          <p>{{ $t('referendum.adminIntro') }}</p>
          <div class="image-wrapper">
            <img :src="require('@/assets/image/admin-user-panel.png')" alt="Panneau d'administration" class="doc-image"
                 loading="lazy"/>
          </div>
        </div>
        <div class="interface-block">
          <h3>{{ $t('referendum.userTitle') }}</h3>
          <p>{{ $t('referendum.userIntro') }}</p>
          <div class="double-image-wrapper">
            <img :src="require('@/assets/image/referendum-view.png')" alt="Referendum view" class="doc-image small"
                 loading="lazy"/>
            <img :src="require('@/assets/image/scrutateur-view.png')" alt="Scrutateur view" class="doc-image small"
                 loading="lazy"/>
          </div>
        </div>
      </div>
    </section>

    <section class="technical-details">
      <h2>{{ $t('referendum.learningsTitle') }}</h2>
      <div class="competencies-grid">
        <InfraCompetencyCard 
          icon="mdi-shield-key-outline"
          title="Cryptographie & Sécurité"
          :description="$t('referendum.learning1')"
        />
        <InfraCompetencyCard 
          icon="mdi-application-cog-outline"
          title="Architecture Logicielle"
          :description="$t('referendum.learning2')"
        />
        <InfraCompetencyCard 
          icon="mdi-git"
          title="Gestion de Projet Agile"
          :description="$t('referendum.learning3')"
        />
        <InfraCompetencyCard 
          icon="mdi-lan-connect"
          title="Communication Réseau"
          :description="$t('referendum.learning4')"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';
import InfraCompetencyCard from '@/component/project/infra/InfraCompetencyCard.vue';

@Component({
  components: {
    InfraCompetencyCard
  }
})
export default class ReferendumComponent extends Vue {
  mounted() {
    setTimeout(() => {
      this.renderDiagrams();
    }, 100);
  }

  async renderDiagrams() {
    try {
      const mermaid = (window as any).mermaid;
      if (!mermaid) return;

      mermaid.initialize({
        startOnLoad: false,
        theme: 'neutral',
        themeVariables: {
          fontFamily: 'TTInterfaces, sans-serif',
          fontSize: '14px',
          primaryColor: '#e8e8e8',
          nodeBorder: '#333',
          mainBkg: '#ffffff',
          textColor: '#333',
          primaryTextColor: '#333',
          nodeTextColor: '#333'
        }
      });

      const votingDiagram = `
sequenceDiagram
    participant V as Électeur
    participant S as Serveur de Vote
    participant SC as Scrutateurs (x3)

    SC->>S: Publication des Clés Publiques ElGamal
    Note over S: Agrégation des clés
    V->>S: Demande de clé publique agrégée
    S-->>V: Envoi PK_agg
    
    rect rgb(230, 245, 255)
    Note over V: Chiffrement du vote : C = (g^r, m * h^r)
    V->>S: Envoi du Bulletin Chiffré (Frame)
    end
    
    S->>S: Stockage du bulletin
    
    Note over S: Fin du scrutin
    S->>SC: Envoi de la somme des bulletins chiffrés
    SC->>S: Envoi des parts de déchiffrement
    S->>S: Reconstruction du résultat final
    S-->>V: Publication des résultats
      `;

      const dockerDiagram = `
graph TB
    subgraph "Client (Dockerized)"
        FC[Front Client<br/>Vue.js] -- "Http" --> VS_C[Vote Service<br/>Spring Boot]
        VS_C -- "Http" --> CS_C[Communication Service<br/>Spring Boot]
    end

    subgraph "Scrutateur (Dockerized)"
        FS[Front Scrutateur<br/>Vue.js] -- "Http" --> VS_S[Vote Service<br/>Spring Boot]
        FS -- "Http" --> DS_S[Decrypt Service<br/>Spring Boot]
        VS_S -- "Http" --> CS_S[Communication Service<br/>Spring Boot]
        DS_S -- "Http" --> CS_S
    end

    subgraph "Server (Dockerized)"
        F_SRV[Front Admin<br/>Vue.js] -- "Http" --> B_SRV[Back Server<br/>Spring Boot]
        B_SRV -- "Database" --> DB[(MySQL DB)]
        CSS[Comm. Service Server<br/>Sproogy Engine] -- "Database" --> DB
    end

    CS_C -- "Socket" --> CSS
    CS_S -- "Socket" --> CSS

    style DB fill:#e1f5fe,stroke:#01579b,color:#01579b
    style CSS fill:#fff9c4,stroke:#fbc02d,color:#fbc02d

      `;

      const {svg: votingSvg} = await mermaid.render('voting-svg', votingDiagram);
      const votingEl = document.getElementById('voting-diagram');
      if (votingEl) votingEl.innerHTML = votingSvg;

      const {svg: dockerSvg} = await mermaid.render('docker-svg', dockerDiagram);
      const dockerEl = document.getElementById('docker-diagram');
      if (dockerEl) dockerEl.innerHTML = dockerSvg;
    } catch (error) {
      console.debug('Mermaid rendering skipped:', error);
    }
  }
}
</script>

<style scoped>
.project-container {
  font-family: 'TTInterfaces', sans-serif;
  line-height: 1.7;
  color: var(--ln-text-primary);
  background-color: transparent;
  width: 100%;
  max-width: var(--ln-content-max-width);
  margin: 0 auto;
  padding: 2rem 1.5rem;
  box-sizing: border-box;
}

.header {
  text-align: center;
  margin-bottom: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-elevated);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-lg);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 12px 48px var(--ln-glass-shadow);
}

.flex-container {
  display: flex;
  align-items: center;
  gap: 3rem;
  flex-wrap: wrap;
  justify-content: center;
}

.image-container {
  width: 180px;
  flex-shrink: 0;
}

.text-container {
  flex: 1 1 400px;
  text-align: left;
}

.header h1 {
  font-size: clamp(2rem, 6vw, 3rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 0.5rem;
  letter-spacing: -1px;
}

.subtitle {
  font-size: 1.25rem;
  color: var(--ln-text-secondary);
  font-weight: 500;
  opacity: 0.9;
}

.header-image {
  width: 100%;
  max-width: 180px;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  transition: transform 0.5s ease;
}

.header-image:hover {
  transform: scale(1.05) rotate(-2deg);
}

section {
  margin-bottom: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-base);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 8px 32px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

section:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 50px var(--ln-glass-shadow-elevated);
}

h2 {
  font-size: clamp(1.5rem, 5vw, 2.2rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 2rem;
  position: relative;
  padding-bottom: 1rem;
}

h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 80px;
  height: 4px;
  background: linear-gradient(to right, var(--ln-teal-deep), var(--ln-terracotta-subtle));
  border-radius: 2px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 3rem;
  align-items: center;
}

.highlight-box {
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border-strong);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-item:last-child {
  margin-bottom: 0;
}

.stat-icon {
  font-size: 2.2rem;
  color: var(--ln-teal-deep);
}

.stat-text {
  font-weight: 600;
  color: var(--ln-text-primary);
}

.tech-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 3rem;
}

.tech-card {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
}

.tech-icon {
  font-size: 3rem;
  color: var(--ln-teal-deep);
}

.tech-text h4 {
  margin: 0 0 1rem 0;
  color: var(--ln-text-primary);
}

.tech-text ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tech-text li {
  font-size: 0.95rem;
  color: var(--ln-text-secondary);
  margin-bottom: 0.5rem;
  position: relative;
  padding-left: 1.2rem;
}

.tech-text li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: var(--ln-teal-deep);
}

.architecture-visual {
  margin-top: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-surface);
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  text-align: center;
}

.docker-visual {
  margin-top: 3rem;
}

.visual-header {
  margin-bottom: 2rem;
}

.visual-badge {
  background: var(--ln-terracotta-subtle);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.visual-caption {
  margin-top: 1.5rem;
  font-size: 0.95rem;
  font-style: italic;
  opacity: 0.8;
}

.code-intro {
  margin-bottom: 2rem;
  color: var(--ln-text-secondary);
  font-size: 1.05rem;
}

.code-container {
  background: #1e2227;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
}

.second-code {
  margin-top: 2rem;
}

[data-theme="light"] .code-container {
  background: #f5f5f5;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
}

.code-header {
  background: #282c34;
  padding: 0.8rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #3e4451;
}

[data-theme="light"] .code-header {
  background: #e0e0e0;
  border-bottom: 1px solid #ccc;
}

.dots {
  display: flex;
  gap: 8px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.red {
  background: #ff5f56;
}

.yellow {
  background: #ffbd2e;
}

.green {
  background: #27c93f;
}

.file-path {
  color: #abb2bf;
  font-size: 0.85rem;
  font-family: monospace;
}

[data-theme="light"] .file-path {
  color: #666;
}

.code-content {
  padding: 1.5rem;
  overflow-x: auto;
}

pre {
  margin: 0;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.9rem;
  color: #abb2bf;
}

[data-theme="light"] pre {
  color: #24292e;
}

.k { color: #c678dd; }
.c { color: #e5c07b; }
.f { color: #61afef; }
.s { color: #98c379; }
.t { color: #5c6370; font-style: italic; }

[data-theme="light"] .k { color: #d73a49; }
[data-theme="light"] .c { color: #6f42c1; }
[data-theme="light"] .f { color: #005cc5; }
[data-theme="light"] .s { color: #22863a; }
[data-theme="light"] .t { color: #6a737d; }

.interface-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
}

.interface-block h3 {
  margin-bottom: 1rem;
  color: var(--ln-text-primary);
}

.interface-block p {
  font-size: 0.95rem;
  color: var(--ln-text-secondary);
  margin-bottom: 1.5rem;
}

.image-wrapper, .double-image-wrapper {
  background: var(--ln-glass-surface);
  padding: 1rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
}

.doc-image {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.doc-image:hover {
  transform: scale(1.02);
}

.double-image-wrapper {
  display: flex;
  gap: 1rem;
}

.doc-image.small {
  flex: 1;
}

.mermaid-container {
  width: 100%;
  overflow-x: auto;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

/* Force #333 text color in Mermaid diagrams ALWAYS (override theme styles) */
.mermaid-container :deep(svg text),
.mermaid-container :deep(svg tspan),
.mermaid-container :deep(foreignObject p),
.mermaid-container :deep(foreignObject span),
.mermaid-container :deep(foreignObject div),
.mermaid-container :deep(.nodeLabel),
.mermaid-container :deep(.edgeLabel) {
  fill: #333 !important;
  color: #333 !important;
}

/* Ensure subgraph titles are also #333 */
.mermaid-container :deep(.cluster-label text) {
  fill: #333 !important;
}

.competencies-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

@media (max-width: 1200px) {
  .competencies-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 992px) {
  .content-grid, .tech-grid, .interface-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .header {
    padding: 1.5rem;
  }

  .flex-container {
    gap: 1rem;
    flex-direction: column;
  }

  .text-container { text-align: center; flex: 1 1 auto; }

  section {
    padding: 1.5rem;
  }

  .competencies-grid {
    grid-template-columns: 1fr;
  }
}
</style>
